import localeEn from '@fullcalendar/core/locales/en-gb'
import localeFi from '@fullcalendar/core/locales/fi'
import localeNl from '@fullcalendar/core/locales/nl'
import localeSv from '@fullcalendar/core/locales/sv'
import Tooltip from '@mui/material/Tooltip'
import { useQuery } from '@tanstack/react-query'
import { DateTime } from 'luxon'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert } from 'react-bootstrap'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useGetCalendarSettings } from '../../api/endpoints/index.ts'
import { personsGetActiveQuery } from '../../api/index.js'
import { FormInput } from '../../common/form/forminput.jsx'
import ResourceTimelineCalendar from '../../common/fullcalendar/ResourceTimelineCalendar.jsx'
import { PageContent } from '../../common/layout/common.js'
import LoadingCentered from '../../common/loaders/loadingcentered.jsx'
import { TimelineEvent } from '../../common/timelinevent/timelineevent.jsx'
import { useCalendarEvents } from '../../hooks/useCalendarEvents.js'
import { useCurrentUser } from '../../hooks/useCurrentUser.ts'
import i18n from '../../i18n.js'
import { CalendarToolbar } from '../../mgmtview/calendar/calendarToolbar.jsx'
import { CalendarHeaderDay, CalendarHeaderWeek } from '../calendar/calendarheaders.jsx'
import { TeamCalendarFilters } from './teamCalendarFilters.jsx'
import { formDefaults, mapPersonToCalendarResource, transformCalendarEvent } from './utils.jsx'

const minusOneDay = (dateStr) => DateTime.fromISO(dateStr).minus({ days: 1 }).toFormat('yyyy-MM-dd')

const localeMap = {
	fi: localeFi,
	sv: localeSv,
	en: localeEn,
	nl: localeNl,
}

const queryKey = 'teamCalendarEvents'

export const TeamCalendar = () => {
	const { t } = useTranslation()
	const currentUser = useCurrentUser()
	const calendarRef = useRef(null)
	const [calendarInstance, setCalendarInstance] = useState(null)
	const [calendarViewRange, setCalendarViewRange] = useState({ start: null, end: null })
	const { data: calendarSettings } = useGetCalendarSettings()

	const today = DateTime.now()

	// Check if calendar should be visible based on settings
	const isCalendarVisible = calendarSettings?.sharedCalendarVisibleOnProfile !== false
	const showOnlyHolidays = calendarSettings?.sharedCalendarShowOnlyHolidays === true

	const [filterOverride, setFilterOverride] = useState(null)
	const [dateRange, setDateRange] = useState(null)
	const [activeEventId, setActiveEventId] = useState(null)

	const form = useForm({
		defaultValues: formDefaults,
		mode: 'onChange',
	})

	const { data: personas } = useQuery(personsGetActiveQuery())

	/** Derive allowed supervisors from currentUser controlDetails and use for filtering logic */
	const allowedSupervisors = useMemo(() => {
		const personNumbers = [] // Use unique personNumbers for filtering since that's what we get from backend for events
		if (currentUser?.controlDetails?.supervisor?.personNumber)
			personNumbers.push(currentUser.controlDetails.supervisor.personNumber)
		if (currentUser?.controlDetails?.supervisor2?.personNumber)
			personNumbers.push(currentUser.controlDetails.supervisor2.personNumber)
		return personNumbers
	}, [currentUser])

	/** Filter personas by supervisor relationships to determine who should be visible in the calendar */
	const allowedPersons = useMemo(() => {
		if (!personas?.data || !currentUser) return []

		return personas.data
			.filter((p) => {
				if (p.personId === currentUser.personId) return true

				const sup1 = p.controlDetails?.supervisor?.personNumber
				const sup2 = p.controlDetails?.supervisor2?.personNumber

				const theirSups = [sup1, sup2].filter(Boolean)
				return theirSups.some((id) => allowedSupervisors.includes(id))
			})
			.map((person) => ({
				id: person.personId.toString(),
				title: person.name,
				nr: person.nr,
				...person,
			}))
	}, [personas, allowedSupervisors, currentUser])

	/** Update dateRange state when calendar view changes */
	const handleDatesSet = ({ startStr, endStr }) => {
		const start = startStr.slice(0, 10)
		const end = minusOneDay(endStr.slice(0, 10))
		setDateRange({ start, end })
		setCalendarViewRange({ start, end })
	}

	const { absenceEvents, holidayDates, isLoading, error } = useCalendarEvents({
		queryKey,
		dateRange,
		enabled: !!dateRange && !!currentUser,
		requestOptions: { team: true },
		holidayRequestOptions: {},
		transformEvent: (row) => transformCalendarEvent(row, currentUser, t),
		t,
		language: i18n.resolvedLanguage,
	})

	const events = useMemo(() => {
		if (showOnlyHolidays) {
			// Cause code is visible for own events. "ABSENCE" for team members' vacations - other cause codes are masked to null
			return absenceEvents.filter((event) => event.causeCode === '21P' || event.causeCode === 'ABSENCE')
		}
		return absenceEvents
	}, [absenceEvents, showOnlyHolidays])

	/** Build calendar resource list from all events (pre-holiday-filter) so the showOnlyHolidays
	 * setting only affects which events are rendered, never which persons appear as rows */
	const personsFromEvents = useMemo(() => {
		if (!absenceEvents || absenceEvents.length === 0 || !personas?.data) return []

		const personIdsFromEvents = new Set(absenceEvents.map((e) => e.personId))

		return personas.data.filter((p) => personIdsFromEvents.has(p.personId)).map(mapPersonToCalendarResource)
	}, [absenceEvents, personas])

	/** Derive active persons list: use filter override when set, otherwise fall back to event-derived list or allowed persons */
	const personsList = useMemo(
		() => filterOverride ?? (personsFromEvents.length > 0 ? personsFromEvents : allowedPersons),
		[filterOverride, personsFromEvents, allowedPersons],
	)

	/** Reset persons list to the event-derived list - used when clearing filters to restore the default view */
	const resetPersonsList = useCallback(() => {
		setFilterOverride(null)
	}, [])

	/**
	 * Filter persons list based on form criteria.
	 * Applies search, supervisor and status filters in sequence.
	 * Falls back to allowedPersons if no events exist in the date range.
	 */
	const submitForm = useCallback(
		(formValues) => {
			let filtered = personsFromEvents.length > 0 ? personsFromEvents : allowedPersons

			const search = (formValues?.search || '').toLowerCase()
			if (search) {
				filtered = filtered.filter((person) => {
					const nameMatch = person.title.toLowerCase().includes(search)
					const nrMatch = person.nr?.toString().includes(search)
					return nameMatch || nrMatch
				})
			}

			const supervisor = formValues?.supervisor?.value
			if (supervisor) {
				filtered = filtered.filter((person) => {
					const sup1 = person.controlDetails?.supervisor?.personNumber
					const sup2 = person.controlDetails?.supervisor2?.personNumber
					return sup1 === supervisor || sup2 === supervisor
				})
			}

			const status = formValues?.status?.value
			if (status) {
				const personIds = new Set(events.filter((e) => e.status === status).map((e) => e.personId))
				filtered = filtered.filter((p) => personIds.has(p.personId))
			}

			const team = formValues?.team
			if (team?.value) {
				// Build from team.members directly — members appear regardless of having events
				const personasByNr = new Map((personas?.data ?? []).map((p) => [p.nr, p]))
				filtered = team.members.map((m) => {
					const full = personasByNr.get(m.nr)
					return full
						? { id: full.personId.toString(), title: full.name, nr: full.nr, ...full }
						: { id: m.id.toString(), title: m.name, nr: m.nr }
				})
			}

			setFilterOverride(filtered)
		},
		[events, personsFromEvents, allowedPersons, personas],
	)

	/** Prevent event interaction in read-only team calendar view */
	const handleEventClick = useCallback(({ jsEvent }) => {
		jsEvent.preventDefault()
		jsEvent.stopPropagation()
	}, [])

	useEffect(() => {
		const handleClickOutside = (e) => {
			if (!e.target.closest('.fc-event, .timeline-event-popover')) {
				setActiveEventId(null)
			}
		}
		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [])

	return (
		<FormProvider {...form}>
			<div style={{ display: 'contents' }}>
				{isLoading && <LoadingCentered show />}
				{error && (
					<Alert variant="danger" className="text-center">
						{error.message}
					</Alert>
				)}
				{!isCalendarVisible && (
					<div className="mt-3">
						<Alert variant="info" className="text-center">
							{t('CalendarHidden', 'Yhteinen kalenteri on piilotettu')}
						</Alert>
					</div>
				)}
				{isCalendarVisible && (
					<PageContent>
						{calendarInstance && <CalendarToolbar calendarRef={calendarInstance} />}
						<TeamCalendarFilters
							resetPersonsList={resetPersonsList}
							submitForm={submitForm}
							currentUser={currentUser}
						/>
						<ResourceTimelineCalendar
							calendarRef={calendarRef}
							onCalendarMount={setCalendarInstance}
							duration={{ weeks: 8 }}
							locale={localeMap[i18n.resolvedLanguage]}
							dateClick={() => setActiveEventId(null)}
							eventClick={handleEventClick}
							eventContent={(event) => {
								const eventData = events?.find(({ id }) => id.toString() === event.event.id)
								if (!eventData) return null
								const isActive = activeEventId?.toString() === eventData.id.toString()
								const isSelf = eventData.personId === currentUser?.personId

								const displayEvent = !isSelf ? { ...eventData, causeCode: null, icdCode: null } : eventData

								return (
									<TimelineEvent
										event={displayEvent}
										isActive={isActive}
										onClose={() => setActiveEventId(null)}
										calendarViewStartDate={calendarViewRange.start}
										calendarViewEndDate={calendarViewRange.end}
										disableButtons={!isSelf}
										isTeam={true}
									/>
								)
							}}
							slotLaneContent={(arg) => {
								const dateStr = DateTime.fromJSDate(arg.date).toFormat('yyyy-MM-dd')
								const holidayName = holidayDates.get(dateStr)
								if (!holidayName) return null
								return (
									<Tooltip title={holidayName} placement="top" followCursor enterDelay={500} leaveDelay={200}>
										<span style={{ position: 'absolute', inset: 0 }} />
									</Tooltip>
								)
							}}
							slotLaneClassNames={(arg) => {
								const dateStr = DateTime.fromJSDate(arg.date).toFormat('yyyy-MM-dd')
								return holidayDates.has(dateStr) ? ['fc-holiday'] : []
							}}
							resources={
								personsList.length
									? personsList
									: [
											{
												id: '1',
												title: t('No employees match the selected filters.'),
												nr: '1',
											},
										]
							}
							resourceAreaHeaderContent={() => (
								<FormInput
									name="search"
									inputProps={{ placeholder: t('Search', 'Haku') }}
									label={t('Search', 'Haku')}
								/>
							)}
							resourceLabelContent={(resource) => (
								<div title={`${resource.resource.title} (${resource.resource.extendedProps.nr})`}>
									{resource.resource.title}
								</div>
							)}
							events={events}
							datesSet={handleDatesSet}
							eventDataTransform={(event) => ({
								...event,
								end: DateTime.fromISO(event.end).plus({ days: 1 }).toFormat('yyyy-MM-dd'),
							})}
							slotLabelContent={(cell) => {
								const cellDate = DateTime.fromJSDate(cell.date)

								if (cell.level === 0) {
									const isCurrentWeek = cellDate.weekNumber === today.weekNumber && cellDate.year === today.year
									const month = cellDate.plus({ days: 3 }).setLocale(i18n.resolvedLanguage).monthLong
									const viewStart = calendarViewRange.start ? DateTime.fromISO(calendarViewRange.start) : null
									const isFocused =
										!isCurrentWeek &&
										viewStart !== null &&
										cellDate.weekNumber === viewStart.weekNumber &&
										cellDate.year === viewStart.year
									return (
										<CalendarHeaderWeek
											isCurrent={isCurrentWeek}
											isFocused={isFocused}
											month={month}
											year={`${cellDate.year}–${String(cellDate.weekNumber).padStart(2, '0')}`}
										/>
									)
								}

								if (cell.level === 1) {
									const dateStr = cellDate.toFormat('yyyy-MM-dd')
									const holidayName = holidayDates.get(dateStr)
									return (
										<CalendarHeaderDay
											isCurrent={cellDate.hasSame(today, 'day')}
											day={holidayName ? `${cellDate.day}.${cellDate.month}` : cellDate.day}
											isHoliday={!!holidayName}
											holidayName={holidayName}
										/>
									)
								}
							}}
						/>
					</PageContent>
				)}
			</div>
		</FormProvider>
	)
}
